import React, {
  useCallback, useMemo, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { VisualizerService } from '../../services';
import {
  IAppReduxState, IAccountReduxState, VisualizerPlanData, VisualizerMonthData, VisualizerDetailsMonthData,
} from '../../models';
import Legend from '../shared/Legend';
import { VisualizerAccordion } from './VisualizerAccordion';
import { VisualizerDescription } from './VisualizerDescription';

const VisualizerBodyContainer = styled('div')({
  margin: '20px 0',
});

const VisualizerLegendContainer = styled('div')(({ theme }) => ({
  paddingBottom: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  marginLeft: '83px',
  [theme.breakpoints.down('sm')]: {
    marginLeft: '55px',
  },
}));

const VisualizerAnnualUsageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  lineHeight: '24px',
  span: {
    paddingLeft: '5px',
  },
}));

const VisualizerExpandDetails = styled('div')(({ theme }) => ({
  width: '40px',
  height: '38px',
  fontSize: '12px',
  fontWeight: '900',
  textAlign: 'center',
  marginRight: '32px',
  [theme.breakpoints.down('sm')]: {
    marginRight: '10px',
  },
  marginLeft: '12px',
}));

const VisualizerLegend = styled(Legend)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  li: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontWeight: '900',
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
    },
    whiteSpace: 'nowrap',
  },
  'li:not(:last-child)': {
    marginRight: '16px',
  },
  span: {
    marginTop: '4px',
    display: 'inline-block',
    height: '10px',
    width: '100%',
    verticalAlign: 'sub',
  },
}));

type VisualizerProps = {
  visualizerOverviewData: VisualizerPlanData<VisualizerMonthData>[] | undefined;
  visualizerDetailsData: VisualizerPlanData<VisualizerDetailsMonthData>[] | undefined;
  visualizerOverviewLoading: boolean;
  visualizerDetailsLoading: boolean;
}

function Visualizer({
  visualizerOverviewData, visualizerDetailsData, visualizerOverviewLoading, visualizerDetailsLoading,
}: VisualizerProps) {
  const intl = useIntl();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const account = useSelector<IAppReduxState, IAccountReduxState>((state) => state.account);
  const selectedMeterId = useSelector<IAppReduxState, string>((state) => state.userInput.selectedMeterID);
  const selectedServicePoint = useMemo(
    () => account?.servicePoints.find((sp) => sp.meterNumber === selectedMeterId),
    [account?.servicePoints, selectedMeterId],
  );
  const [planCode, setPlanCode] = useState<string | undefined>();

  useEffect(() => {
    setPlanCode(selectedServicePoint?.currentPlanCode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServicePoint?.meterNumber]);

  const optionsList = useMemo(
    () => (visualizerOverviewData ? visualizerOverviewData.map((plan) => plan.plan) : []),
    [visualizerOverviewData],
  );

  const planData = useMemo(
    () => (visualizerOverviewData ? visualizerOverviewData.find((x) => x.plan === planCode) : undefined),
    [visualizerOverviewData, planCode],
  );
  const detailsPlanData = useMemo(() => (
    visualizerDetailsData ? visualizerDetailsData.find((x) => x.plan === planCode) : undefined
  ), [visualizerDetailsData, planCode]);

  const annualUsage = useMemo(() => planData && VisualizerService.calculateAnnualUsage(planData), [planData]);
  const legendData = useMemo(() => (planData ? VisualizerService.getLegend(planData.data, intl) : []), [intl, planData]);

  const selectChangeHandler = useCallback((event: SelectChangeEvent<string>) => {
    setPlanCode(`${event.target.value}`);
  }, []);

  return (
    <>
      <Grid
        container
        sx={{
          padding: {
            xs: '40px 20px 20px 20px',
            sm: '40px 40px 20px 40px',
          },
          borderBottom: '1px solid lightgray',
        }}
        direction="column"
        rowGap="30px"
      >
        <Grid item>
          <VisualizerDescription />
        </Grid>
        <Grid item>
          <FormControl
            fullWidth={isSmallScreen}
            sx={{
              width: {
                sm: '350px',
                md: '350px',
                lg: '350px',
                xl: '350px',
              },
            }}
          >
            <InputLabel
              id="visualizer-plan-select-label"
            >
              <FormattedMessage id="visualizer-actions.header" />
            </InputLabel>
            <Select
              data-testid="visualizer-plan-select"
              labelId="visualizer-plan-select-label"
              id="visualizer-plan-select"
              value={planCode || ''}
              label={intl.formatMessage({
                id: 'visualizer-actions.header',
              })}
              onChange={selectChangeHandler}
              defaultValue={selectedServicePoint?.currentPlanCode}
              disabled={visualizerOverviewLoading}
              sx={{
                '& .MuiSelect-select span': {
                  whiteSpace: 'break-spaces',
                },
              }}
            >
              {!visualizerOverviewLoading ? (
                optionsList?.map((opt) => (
                  <MenuItem
                    style={{ whiteSpace: 'normal' }}
                    value={opt}
                    key={opt}
                    data-testid="visualizerPlanOption"
                  >
                    <span style={{ fontSize: '14px', color: '#000000' }}>
                      <FormattedMessage id={`${opt}-plan-label`} />
                    </span>
                  </MenuItem>
                ))
              ) : (
                <MenuItem
                  style={{ whiteSpace: 'normal' }}
                  value={selectedServicePoint?.currentPlanCode}
                  key="loading"
                  data-testid="visualizerPlanOption"
                >
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <VisualizerAnnualUsageContainer>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              <FormattedMessage id="visualizer-description.annual-usage" />
            </Typography>
            {visualizerOverviewLoading && (
              <Skeleton variant="text" sx={{ fontSize: '1rem', width: '48px', marginLeft: '5px' }} />
            )}
            {planData && !visualizerOverviewLoading && (
              <span data-testid="annual-usage-span">
                {`${annualUsage} kWh`}
              </span>
            )}
          </VisualizerAnnualUsageContainer>
        </Grid>
      </Grid>
      {visualizerOverviewLoading ? (
        <Grid
          container
          sx={{ margin: '24px 0' }}
        >
          <Grid
            item
            display="flex"
            justifyContent="center"
            md
            sm
            xs
          >
            <CircularProgress color="primary" size="50px" title="Loading visualizer data..." />
          </Grid>
        </Grid>
      ) : (
        <VisualizerBodyContainer data-testid="visualizer-charts-body">
          {planData && (
          <VisualizerLegendContainer data-testid="visualizer-legend-container">
            <VisualizerLegend legendItems={legendData} />
            <VisualizerExpandDetails>
              <FormattedMessage id="visualizer-legend.expand-details" />
            </VisualizerExpandDetails>
          </VisualizerLegendContainer>
          )}
          {planData && planData.data.map((month, index) => (
            <VisualizerAccordion
              key={month.month}
              month={month}
              detailsMonth={detailsPlanData?.data[index]}
              detailsLoading={visualizerDetailsLoading}
              maxWidth={VisualizerService.calculatelargestWidth(planData)}
            />
          ))}
        </VisualizerBodyContainer>
      )}
    </>
  );
}

export { Visualizer };
